import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const BOIRDueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form W-2 Deadlines for Tax Year 2025 | TaxZerone"
          description=" Learn the Form W-2 deadline for Tax Year 2025. Stay compliant by understanding key due dates and filing requirements with clear guidance from TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-w-2-due-dates/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 2,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form W-2 </span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Employers must file W-2 forms on time to stay compliant with IRS
          regulations.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2duedates`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-w-2-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form W-2 due dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {'Employers generally must '}
                        <Link
                          href="/information-returns/efile-irs-form-w2/"
                          sx={{ color: '#0202ff' }}
                        >
                          file Form W-2
                        </Link>
                        {
                          ' if they withheld federal income, Social Security, or Medicare taxes, would have withheld under Form W-4 rules, or paid an employee '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          $600 or more
                        </strong>
                        {' in wages.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {
                          'Employers must furnish Form W-2 to employees and file it with the '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          Social Security Administration (SSA)
                        </strong>
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>
                        {
                          ' (or the next business day if January 31 falls on a weekend or federal holiday) for both paper and electronic filing.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {
                          'State W-2 filing deadlines vary, with most states following the federal deadline, while some have different due dates or do not require separate state filing. '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        {
                          'Late, incorrect, or incomplete W-2 filings may result in IRS penalties ranging from '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per form
                        </strong>
                        , depending on how late the forms are filed and whether
                        the failure was due to intentional disregard.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies.'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box />
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Form W-2 (<b>Wage and Tax Statement</b>) is a tax document
                  that U.S. employers must provide each employee by the end of
                  January. It reports an employee&apos;s total wages and the
                  taxes withheld from their paycheck during the year. The form
                  is also submitted to the IRS and the Social Security
                  Administration (SSA).
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Employers must file Form(s) W-2 if you have one or more
                  employees to whom you made payments (including noncash
                  payments) for the employees’ services in your trade or
                  business during 2024. Complete and{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    file W2 form online
                  </Link>{' '}
                  for each employee for whom any of the following applies (even
                  if the employee is related to you).
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    You withheld any income, social security, or Medicare tax
                    from wages regardless of the amount of wages; or
                  </li>
                  <li>
                    You would have had to withhold income tax if the employee
                    had claimed no more than one withholding allowance (for 2019
                    or earlier Forms W-4) or had not claimed exemption from
                    withholding on Form W-4; or
                  </li>
                  <li>
                    You paid $600 or more in wages even if you did not withhold
                    any income, social security, or Medicare tax.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{ fontSize: '19px !important', fontWeight: 600 }}
                >
                  Key Components:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Displays total wages and salary earned during the year
                  </li>
                  <li>Reports federal, state, and local taxes withheld</li>
                  <li>Lists Social Security and Medicare contributions</li>
                  <li>
                    Includes details on benefits and retirement contributions
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is the deadline for employers to file W-2s?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Employers must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    file W-2 forms
                  </Link>{' '}
                  with the Social Security Administration (SSA) by{' '}
                  <b>February 2</b>. This applies to both electronic and paper
                  filings.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Additionally, employers are required to provide employees with
                  their W-2 forms by <b>February 2</b> to ensure employees can
                  file their annual tax returns on time.
                </Typography>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Ready to Submit your W-2 Forms for Federal and State?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0 }}>
                      Get started with <b>TaxZerone</b>, your complete solution
                      for all W-2 filing requirements.
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      {' '}
                      <img
                        src="/assets/images/deadline-jan.webp"
                        alt="Deadline"
                        loading="lazy"
                        width={90}
                        height={102}
                      />{' '}
                    </Box>
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        color: '#333',
                        '&:hover, &:focus, &:active': {
                          color: '#fff',
                          backgroundColor: '#172b6b',
                        },
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2duedates`}
                    >
                      E-File Now!
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How can employers extend the W-2 form deadline?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  As an employer, meeting tax deadlines is critical, but
                  unexpected delays can occur. If you need more time to{' '}
                  <b>file W-2 forms</b> with the Social Security Administration
                  (SSA), you can request a <b>30-day extension</b> by submitting{' '}
                  <b>
                    <Link
                      href="/tax-extension/efile-irs-form-8809-online/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 8809
                    </Link>
                    , Application for Extension of Time to File Information
                    Returns
                  </b>
                  . The request must be submitted before the original deadline,
                  and the extension applies to the filing.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mt: 2, mb: 4 }}>
                  <b>Note:</b> This does not extend the deadline for providing
                  W-2 forms to employees.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is the deadlines for submitting Form W-2 to the state?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For most states, employers must submit{' '}
                  <Link href="/form-w2-state-filing/" sx={{ color: '#0202ff' }}>
                    Form W-2s
                  </Link>{' '}
                  to state tax authorities by <b>January 31</b> of each year.
                  This is the same deadline as the{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    federal W-2 filing
                  </Link>{' '}
                  deadline with the Social Security Administration. However,
                  some states may have slightly different deadlines or
                  additional requirements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To ensure compliance, employers should refer to the table
                  below for state-specific filing information.
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            State Name
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Abbreviation
                          </TableCell>
                          <TableCell sx={{ width: 300, fontWeight: 600 }}>
                            Filing Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/alabama-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Alabama
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            AL
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Alaska
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            AK
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/arizona-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Arizona
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            AZ
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/arkansas-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Arkansas
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            AR
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            {' '}
                            California
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            CA
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/colorado-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Colorado
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            CO
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/connecticut-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Connecticut
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            CT
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/delaware-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Delaware
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            DE
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/district-of-columbia-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              District of Columbia
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            DC
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Florida
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            FL
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/georgia-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Georgia
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            GA
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/hawaii-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Hawaii
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            HI
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/idaho-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Idaho
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            ID
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/illinois-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Illinois
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IL
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/indiana-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Indiana
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IN
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/iowa-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Iowa
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IA
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/kansas-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Kansas
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            KS
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/kentucky-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Kentucky
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            KY
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/louisiana-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Louisiana
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            LA
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/maine-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Maine
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            ME
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/maryland-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Maryland
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            MD
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/massachusetts-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Massachusetts
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            MA
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/michigan-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Michigan
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            MI
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/minnesota-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Minnesota
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            MN
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/mississippi-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Mississippi
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            MS
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/missouri-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Missouri
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            MO
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/montana-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Montana
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            MT
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/nebraska-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Nebraska
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            NE
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Nevada
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            NV
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            New Hampshire
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            NH
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/new-jersey-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              New Jersey
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            NJ
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            February 15
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/new-mexico-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              New Mexico
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            NM
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            New York
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            NY
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/north-carolina-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              North Carolina
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            NC
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/north-dakota-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              North Dakota
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            ND
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/ohio-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Ohio
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            OH
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/oklahoma-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Oklahoma
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            OK
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/oregon-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Oregon
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            OR
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/pennsylvania-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              Pennsylvania
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            PA
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/rhode-island-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Rhode Island
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            RI
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/south-carolina-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              South Carolina
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            SC
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            South Dakota
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            SD
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Tennessee
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            TN
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Texas
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            TX
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/utah-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Utah
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            UT
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/vermont-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Vermont
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            VT
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/virginia-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Virginia
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            VA
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Washington
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            WA
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/west-virginia-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              West Virginia
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            WV
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <Link
                              href="/form-w2-state-filing/wisconsin-w-2-filing-requirements/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Wisconsin
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            WI
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            {' '}
                            Wyoming
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            WY
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filing Not Required
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 4,
                  }}
                >
                  Penalties for Late W-2 Filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  As an employer, timely filing of W-2 forms is essential to
                  avoid penalties. Failing to file on time can result in
                  significant fines, which vary depending on how late the filing
                  is. Here’s what employers should be aware of regarding
                  penalties for late W-2 filing:
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Tax Year
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Up to 30 Days Late
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            31 Days Late Through August 1
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            After August 1 or Not Filed
                          </TableCell>
                          <TableCell sx={{ width: 300, fontWeight: 600 }}>
                            Intentional Disregard
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            2026
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $60
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $130
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $340
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>$680</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            2025
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $60
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $130
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $330
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>$660</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            2024
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $60
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $120
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $310
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>$630</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            2023
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $50
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $110
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $290
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>$580</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            2022
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $50
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $110
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $280
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>$570</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            2021
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $50
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $110
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $280
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                            }}
                          >
                            $560
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Qualifying for Penalty Relief
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You may be eligible for penalty relief if you can demonstrate
                  that you took reasonable steps and acted responsibly but were
                  still unable to file your return or pay your taxes on time.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Some valid reasons for missing the deadline could include:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Fires, natural disasters, or civil disturbances
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Difficulty obtaining necessary records
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Death, serious illness, or unavoidable absence of the
                    taxpayer or an immediate family member
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    System issues that prevented timely electronic filing or
                    payment
                  </li>
                </ul>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above details were sourced from the IRS regarding
                        the W-2 form due date.{' '}
                        <ul>
                          <li>
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/iw2w3.pdf"
                              sx={{ color: '#0202ff' }}
                              target="_blank"
                            >
                              General Instructions for Forms W-2
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="https://www.irs.gov/payments/information-return-penalties"
                              sx={{ color: '#0202ff' }}
                              target="_blank"
                            >
                              Information return penalties
                            </Link>
                          </li>
                        </ul>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Looking to File Your W-2 Forms for Federal and State?
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              E-filing your <b>W-2 forms</b> with TaxZerone ensures a smooth
              filing experience,{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              with support for <b>both federal and state submissions</b>, as
              well as <b>postal mailing</b> for recipients&apos; copies.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2duedates`}
              >
                E-file W-2 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default BOIRDueDates;
